/**
 * 审批明细表（DETAIL）共享工具
 * - 行数规则：有效最小行数 = max(minRows, required ? 1 : 0)
 * - computed 表达式：仅数字字面量、字段引用、+ - * / 与括号，自实现递归下降解析
 * - 表达式只能引用本行非 computed 的 NUMBER/MONEY 兄弟字段（前后端同一规则）
 */

// DETAIL 容器内允许的基础子字段类型（禁止嵌套 DETAIL）
export const DETAIL_CHILD_TYPES = [
  'TEXT', 'TEXTAREA', 'NUMBER', 'MONEY', 'DATE', 'DATETIME',
  'SELECT', 'RADIO', 'CHECKBOX', 'EMAIL', 'PHONE', 'FILE'
]

/**
 * 判断字段是否为 DETAIL 明细表容器
 * @param {Object} field 表单字段定义
 * @returns {boolean} type 为 'DETAIL' 时返回 true
 */
export function isDetailField(field) {
  return field?.type === 'DETAIL'
}

/**
 * 有效最小行数：required=true 时至少 1 行
 * @param {Object} field DETAIL 字段定义（含 minRows、required）
 * @returns {number} 有效最小行数，即 max(minRows, required ? 1 : 0)；minRows 非法时按 0
 */
export function getEffectiveMinRows(field) {
  const minRows = Number(field?.minRows)
  const base = Number.isFinite(minRows) && minRows >= 0 ? Math.floor(minRows) : 0
  return field?.required ? Math.max(base, 1) : base
}

/**
 * 有效最大行数：null/undefined/'' 语义为"不限"，返回 null
 * 注意不能裸用 Number(field.maxRows) 判空：Number(null) === 0 会把"不限"误判成上限 0
 * @param {Object} field DETAIL 字段定义（含 maxRows）
 * @returns {number|null} 有效最大行数（向下取整）；空值或非法数值返回 null（不限）
 */
export function getEffectiveMaxRows(field) {
  const raw = field?.maxRows
  if (raw === null || raw === undefined || raw === '') return null
  const maxRows = Number(raw)
  return Number.isFinite(maxRows) ? Math.floor(maxRows) : null
}

/**
 * 金额收敛到 2 位小数（与后端 MONEY scale=2 对齐的展示/提交口径）
 * 按绝对值舍入后还原符号，对齐后端 BigDecimal HALF_UP 对负数"远离零"的方向，
 * 负数金额（红冲/折扣行）与后端重算结果一致。
 * EPSILON 为无条件上抬的近似技巧：对恰在半分位之下的值（如 1.0049999999999999）
 * 存在理论误差，属可接受的前端兜底，精确口径以后端 BigDecimal 为准。
 * `value || 1` 用于避免 value 为 -0/0 时 Math.sign 返回 -0 的边界。
 * @param {number} value 待收敛金额
 * @returns {number} 收敛到 2 位小数的金额；非有限数返回 0
 */
export function roundMoney(value) {
  if (!Number.isFinite(value)) return 0
  return Math.sign(value || 1) * Math.round((Math.abs(value) + Number.EPSILON) * 100) / 100
}

/**
 * 列合计：非数值一律按 0 计入
 * 返回未收敛的浮点累加值；MONEY 列写入 formData[targetKey] 前调用方需再套
 * roundMoney（NUMBER 列不收敛，位数不限）。
 * @param {Array<Object>} rows 明细行数组
 * @param {string} columnKey 参与合计的列字段 key
 * @returns {number} 该列浮点累加值；rows 非数组时返回 0
 */
export function sumDetailColumn(rows, columnKey) {
  if (!Array.isArray(rows)) return 0
  return rows.reduce((acc, row) => {
    const num = Number(row?.[columnKey])
    return acc + (Number.isFinite(num) ? num : 0)
  }, 0)
}

function tokenize(expr) {
  const tokens = []
  let i = 0
  while (i < expr.length) {
    const ch = expr[i]
    if (/\s/.test(ch)) { i += 1; continue }
    if (/[0-9.]/.test(ch)) {
      let j = i
      while (j < expr.length && /[0-9.]/.test(expr[j])) j += 1
      const num = Number(expr.slice(i, j))
      if (!Number.isFinite(num)) throw new Error(`非法数字：${expr.slice(i, j)}`)
      tokens.push({ type: 'num', value: num })
      i = j
      continue
    }
    if (/[A-Za-z_$]/.test(ch)) {
      let j = i
      while (j < expr.length && /[A-Za-z0-9_$]/.test(expr[j])) j += 1
      tokens.push({ type: 'ident', value: expr.slice(i, j) })
      i = j
      continue
    }
    if ('+-*/()'.includes(ch)) {
      tokens.push({ type: ch })
      i += 1
      continue
    }
    throw new Error(`不支持的字符：${ch}`)
  }
  return tokens
}

function parse(tokens) {
  let pos = 0
  function peek() { return tokens[pos] }
  function parseExpr() {
    let node = parseTerm()
    while (peek() && (peek().type === '+' || peek().type === '-')) {
      const op = tokens[pos].type
      pos += 1
      node = { op, left: node, right: parseTerm() }
    }
    return node
  }
  function parseTerm() {
    let node = parseFactor()
    while (peek() && (peek().type === '*' || peek().type === '/')) {
      const op = tokens[pos].type
      pos += 1
      node = { op, left: node, right: parseFactor() }
    }
    return node
  }
  function parseFactor() {
    const token = tokens[pos]
    if (!token) throw new Error('表达式不完整')
    if (token.type === '-') {
      pos += 1
      return { op: 'neg', value: parseFactor() }
    }
    if (token.type === 'num') { pos += 1; return { num: token.value } }
    if (token.type === 'ident') { pos += 1; return { ref: token.value } }
    if (token.type === '(') {
      pos += 1
      const node = parseExpr()
      if (!peek() || peek().type !== ')') throw new Error('括号不匹配')
      pos += 1
      return node
    }
    throw new Error(`无法解析：${token.value || token.type}`)
  }
  const ast = parseExpr()
  if (pos < tokens.length) throw new Error('表达式存在多余内容')
  return ast
}

function collectRefs(ast, refs = new Set()) {
  if (!ast) return refs
  if (ast.ref) refs.add(ast.ref)
  if (ast.left) collectRefs(ast.left, refs)
  if (ast.right) collectRefs(ast.right, refs)
  if (ast.value && typeof ast.value === 'object') collectRefs(ast.value, refs)
  return refs
}

/**
 * 校验 computed 表达式：语法合法 + 只引用本行非 computed 的 NUMBER/MONEY 兄弟字段
 * @returns {string} 空串表示合法，否则为中文错误信息
 */
export function validateComputedExpression(expr, children) {
  if (!expr || !expr.trim()) return '表达式不能为空'
  let ast
  try {
    ast = parse(tokenize(expr))
  } catch (error) {
    return `表达式语法错误：${error.message}`
  }
  for (const ref of collectRefs(ast)) {
    const target = (children || []).find(c => c.key === ref)
    if (!target) return `表达式引用了不存在的字段：${ref}`
    if (!['NUMBER', 'MONEY'].includes(target.type)) return `只能引用数值/金额字段：${ref}`
    if (target.computed) return `不能引用其他计算字段：${ref}`
  }
  return ''
}

function evalAst(ast, row) {
  if ('num' in ast) return ast.num
  if ('ref' in ast) {
    const value = Number(row?.[ast.ref])
    return Number.isFinite(value) ? value : 0
  }
  if (ast.op === 'neg') return -evalAst(ast.value, row)
  const left = evalAst(ast.left, row)
  const right = evalAst(ast.right, row)
  switch (ast.op) {
    case '+': return left + right
    case '-': return left - right
    case '*': return left * right
    case '/':
      if (right === 0) throw new Error('除数不能为 0')
      return left / right
    default:
      throw new Error('未知运算符')
  }
}

/**
 * computed 表达式求值：引用缺失/非数值按 0 参与计算
 * @param {string} expr 表达式（仅数字字面量、字段引用、+ - * / 与括号）
 * @param {Object} row 当前明细行数据（key → 值）
 * @returns {number} 求值结果
 * @throws {Error} 语法错误（非法字符/括号不匹配/表达式不完整等）或除零时抛出
 */
export function evaluateComputedExpression(expr, row) {
  return evalAst(parse(tokenize(expr)), row)
}
